import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import 'bootstrap/dist/css/bootstrap.min.css';
import "./index.css";
import { loadConfig } from "./config";
import axios from "axios";

const root = ReactDOM.createRoot(document.getElementById("root"));
const REQUEST_STATE_EVENT = "app-request-state";
const REQUEST_SESSION_KEY = "app_request_session_id";

const createSessionId = () => `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getRequestSessionId = () => {
    if (typeof window === "undefined" || !window.sessionStorage) return createSessionId();
    let sessionId = window.sessionStorage.getItem(REQUEST_SESSION_KEY);
    if (!sessionId) {
        sessionId = createSessionId();
        window.sessionStorage.setItem(REQUEST_SESSION_KEY, sessionId);
    }
    return sessionId;
};

const requestStore = {
    active: new Map(),
    lastChangeAt: 0
};

const getRequestLabel = (config = {}) => {
    if (config.requestLabel) return String(config.requestLabel);
    const method = String(config.method || "GET").toUpperCase();
    const rawUrl = String(config.url || "");
    const path = rawUrl.split("?")[0];
    if (path.includes("/reports/")) return "Generating report";
    if (method === "POST") return "Saving data";
    if (method === "PUT") return "Updating data";
    if (method === "DELETE") return "Deleting data";
    if (method === "GET") return "Loading data";
    return "Processing request";
};

const broadcastRequestState = () => {
    if (typeof window === "undefined") return;
    const list = Array.from(requestStore.active.values());
    const latest = list.length > 0 ? list[list.length - 1] : null;
    window.dispatchEvent(new CustomEvent(REQUEST_STATE_EVENT, {
        detail: {
            activeCount: list.length,
            label: latest?.label || "",
            startedAt: latest?.startedAt || null,
            sessionId: getRequestSessionId(),
            lastChangeAt: requestStore.lastChangeAt
        }
    }));
};

const removeTrackedRequest = (config) => {
    const requestId = config?.metadata?.requestId;
    if (!requestId) return;
    requestStore.active.delete(requestId);
    requestStore.lastChangeAt = Date.now();
    broadcastRequestState();
};

axios.defaults.timeout = 0;

// Load config.xml before rendering the application
root.render(
    <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
            <div className="spinner-border" role="status" />
            <div className="mt-3">Loading...</div>
        </div>
    </div>
);

loadConfig()
    .catch(() => undefined)
    .finally(() => {
        if (typeof window !== "undefined" && window.location && window.location.hostname) {
            axios.defaults.headers.common["X-Tenant-Host"] = window.location.hostname;
        }
        axios.defaults.headers.common["X-Request-Session"] = getRequestSessionId();
        const token = localStorage.getItem("token");
        if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        axios.interceptors.request.use(
            (config) => {
                const requestId = createSessionId();
                const label = getRequestLabel(config);
                config.metadata = {
                    ...(config.metadata || {}),
                    requestId,
                    startedAt: Date.now(),
                    label
                };
                config.headers = config.headers || {};
                config.headers["X-Request-Session"] = getRequestSessionId();
                config.headers["X-Request-Id"] = requestId;
                requestStore.active.set(requestId, { label, startedAt: Date.now() });
                requestStore.lastChangeAt = Date.now();
                broadcastRequestState();
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
        axios.interceptors.response.use(
            (response) => {
                removeTrackedRequest(response.config);
                return response;
            },
            (error) => {
                removeTrackedRequest(error.config);
                return Promise.reject(error);
            }
        );
        root.render(<App />);
    });
